import { useCallback, useMemo, useState } from 'react';
import { Box } from '@optiaxiom/react';
import { ConnectionMode, Edge, MiniMap, Node, ReactFlow, ViewportPortal } from '@xyflow/react';

import { ExecutionDetails } from '@frontend/types/agentExecution';

import { useStepExecutions } from '../../hooks/useStepExecutions';
import { CustomEdge } from '../workflow-agent/edges';
import { useWorkflow } from '../workflow-agent/hooks/useWorkflow';
import { NodeTypeCategory } from '../workflow-agent/types/connections';

import {
  canvasContainer,
  infiniteBackground,
  reactFlow,
  reactFlowWrapper,
} from './ExecutionCanvas.css';

import '@xyflow/react/dist/style.css';

type ExecutionCanvasProps = {
  nodes: Node[];
  edges: Edge[];
  stepExecutions: unknown[];
  executionDetails: ExecutionDetails;
  onStepClick?: (stepId: string) => void;
  selectedStepId?: string;
};

const edgeTypes = {
  custom: CustomEdge,
};

export function ExecutionCanvas({
  nodes,
  edges,
  executionDetails,
  onStepClick,
  selectedStepId,
}: ExecutionCanvasProps) {
  const { nodeTypes, getNodeConfig } = useWorkflow();

  const [localSelectedStepId, setLocalSelectedStepId] = useState<string | undefined>(
    selectedStepId
  );

  // Filter out trigger nodes from workflow execution view
  const filteredNodes = useMemo(() => {
    return nodes.filter(node => {
      const nodeType = getNodeConfig(
        node.type || ((node.data as Record<string, unknown>)?.type as string)
      );
      const isTriggerNode = nodeType?.description?.category === NodeTypeCategory.TRIGGER;
      return !isTriggerNode;
    });
  }, [nodes, getNodeConfig]);

  // Use the step executions hook to get nodes with status
  const { nodesWithStatus } = useStepExecutions(
    filteredNodes,
    executionDetails,
    localSelectedStepId
  );

  const handleNodeClick = useCallback(
    (event: MouseEvent, node: Node) => {
      event.stopPropagation();
      const stepId = node.id;
      setLocalSelectedStepId(stepId);

      if (onStepClick) {
        onStepClick(stepId);
      }
    },
    [onStepClick]
  );

  return (
    <Box className={canvasContainer}>
      <div className={reactFlowWrapper}>
        <ReactFlow
          nodes={nodesWithStatus}
          edges={edges}
          onNodeClick={handleNodeClick}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          nodesDraggable={false}
          elementsSelectable
          panOnScroll
          panOnDrag
          zoomOnDoubleClick
          className={reactFlow}
          connectionMode={ConnectionMode.Strict}
          proOptions={{
            hideAttribution: true,
          }}
        >
          <ViewportPortal>
            <Box className={infiniteBackground} />
          </ViewportPortal>

          <MiniMap position="bottom-right" />
        </ReactFlow>
      </div>
    </Box>
  );
}
